window.VAE = window.VAE || {};

VAE.AnalysisDisplay = (function () {
  'use strict';

  // Helper function to get the target element consistently
  function getAutoSectionElement() {
    const element = document.getElementById('vae-auto-section-content');
    if (!element) {
      console.error('VAE.AnalysisDisplay: Critical UI element #vae-auto-section-content not found.');
      // In a more robust setup, we might throw an error or have a fallback
    }
    return element;
  }

  // Helper to format WCAG criteria
  function formatWcag(wcagCriterion) {
    if (!wcagCriterion) {
      return 'N/A'; // Changed from 'Keine WCAG-Referenz' for brevity
    }
    if (Array.isArray(wcagCriterion)) {
      return `WCAG ${wcagCriterion.join(', ')}`;
    }
    return `WCAG ${wcagCriterion}`;
  }

  // Helper to create collapsible sections
  function createCollapsibleSection(title, items, className, defaultOpen = false, itemRenderer) {
    if (!items || items.length === 0) return null;

    const section = document.createElement('div');
    section.className = 'vae-collapsable-section ' + (className || '');

    const header = document.createElement('button');
    header.className = 'vae-collapsable-header';
    header.setAttribute('aria-expanded', defaultOpen ? 'true' : 'false');
    header.innerHTML = `
      <div class="vae-section-title">
        <span class="vae-section-icon">
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" width="16" height="16">
            <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"></path>
          </svg>
        </span>
        ${title} <span class="vae-section-count">(${items.length})</span>
      </div>
    `;

    const content = document.createElement('div');
    content.className = 'vae-collapsable-content';
    content.style.display = defaultOpen ? 'block' : 'none';

    const list = document.createElement('ul');
    list.className = 'vae-fix-details-list'; // Shared class name for styling

    items.forEach(itemData => {
      const listItem = itemRenderer(itemData);
      if (listItem) list.appendChild(listItem);
    });

    content.appendChild(list);

    header.addEventListener('click', function () {
      const isExpanded = this.getAttribute('aria-expanded') === 'true';
      this.setAttribute('aria-expanded', !isExpanded);
      content.style.display = isExpanded ? 'none' : 'block';
    });

    section.appendChild(header);
    section.appendChild(content);
    return section;
  }

  // Helper to create WCAG criteria statistics
  function createWcagCriteriaStats(issues) {
    const wcagStats = {};
    
    issues.forEach(issue => {
      // T-006.3.1: Handle both single criterion and multi-criterion issues
      const criteria = Array.isArray(issue.wcagCriterion) ? issue.wcagCriterion : [issue.wcagCriterion || 'Unknown'];
      
      criteria.forEach(wcagCriterion => {
        if (!wcagStats[wcagCriterion]) {
          wcagStats[wcagCriterion] = {
            total: 0,
            critical: 0,
            high: 0,
            medium: 0,
            low: 0,
            unknown: 0,
            // T-006.3.2: Source tracking for Axe-Core vs. LLM-Found issues
            axeCoreCount: 0,
            llmFoundCount: 0,
            multiCriterionCount: 0 // Track issues affecting multiple criteria
          };
        }
        
        wcagStats[wcagCriterion].total++;
        const severity = (typeof issue.impact === 'string' ? issue.impact.toLowerCase() : 'unknown');
        wcagStats[wcagCriterion][severity] = (wcagStats[wcagCriterion][severity] || 0) + 1;
        
        // T-006.3.2: Source attribution
        if (issue.axeCoreSource === true) {
          wcagStats[wcagCriterion].axeCoreCount++;
        } else if (issue.axeCoreSource === false) {
          wcagStats[wcagCriterion].llmFoundCount++;
        }
        
        // T-006.3.1: Multi-criterion tracking
        if (Array.isArray(issue.wcagCriterion) && issue.wcagCriterion.length > 1) {
          wcagStats[wcagCriterion].multiCriterionCount++;
        }
      });
    });

    return wcagStats;
  }

  // Helper to render WCAG criteria breakdown
  function renderWcagCriteriaBreakdown(wcagStats, allIssues) {
    const criteriaContainer = document.createElement('div');
    criteriaContainer.className = 'vae-wcag-criteria-breakdown';

    const header = document.createElement('h5');
    header.textContent = 'Aufschlüsselung nach WCAG-Kriterien:';
    header.style.marginBottom = '10px';
    criteriaContainer.appendChild(header);

    return criteriaContainer;
  }

  // Helper function to get severity colors
  function getSeverityColor(severity) {
    const severityLower = (severity || '').toLowerCase();
    switch (severityLower) {
      case 'critical': return '#dc3545';
      case 'high': return '#fd7e14';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  }

  // Helper function to get severity text colors
  function getSeverityTextColor(severity) {
    const severityLower = (severity || '').toLowerCase();
    switch (severityLower) {
      case 'medium': return 'black';
      default: return 'white';
    }
  }

  // Helper function to extract VAE-ID from various sources
  function extractVaeId(item) {
    // First try the direct vaeId field
    if (item.vaeId) {
      return item.vaeId;
    }
    
    // Try to extract from selector if it contains data-vae-id
    const selector = item.selector || item.target || '';
    if (selector) {
      // Match patterns like [data-vae-id='vae-87'] or [data-vae-id="vae-87"]
      const vaeIdMatch = selector.match(/\[data-vae-id=['"]([^'"]+)['"]\]/);
      if (vaeIdMatch) {
        return vaeIdMatch[1];
      }
      
      // Match patterns like data-vae-id='vae-87' (without brackets)
      const vaeIdMatch2 = selector.match(/data-vae-id=['"]([^'"]+)['"]/);
      if (vaeIdMatch2) {
        return vaeIdMatch2[1];
      }
    }
    
    return null;
  }

  // Helper function to display VAE-ID with fallback
  function displayVaeId(item) {
    const vaeId = extractVaeId(item);
    return vaeId || 'Keine VAE-ID';
  }

  // Helper to create WCAG criteria statistics for fixes (with success/failure breakdown)
  function createWcagFixStats(resultsDetails) {
    const wcagStats = {};
    
    resultsDetails.forEach(detail => {
      // T-006.3.1: Handle both single criterion and multi-criterion fix results
      const criteria = Array.isArray(detail.wcagCriterion) ? detail.wcagCriterion : [detail.wcagCriterion || 'Unknown'];
      
      criteria.forEach(wcagCriterion => {
        if (!wcagStats[wcagCriterion]) {
          wcagStats[wcagCriterion] = {
            total: 0,
            successful: 0,
            failed: 0,
            successBySeverity: { critical: 0, high: 0, medium: 0, low: 0, unknown: 0 },
            failedBySeverity: { critical: 0, high: 0, medium: 0, low: 0, unknown: 0 },
            // T-006.3.2: Source tracking for fixes
            axeCoreFixCount: 0,
            llmFixCount: 0,
            multiCriterionFixCount: 0,
            batchFixCount: 0 // Track fixes that were part of batch processing
          };
        }
        
        wcagStats[wcagCriterion].total++;
        const severity = (typeof detail.severity === 'string' ? detail.severity.toLowerCase() : 'unknown');
        
        if (detail.success) {
          wcagStats[wcagCriterion].successful++;
          wcagStats[wcagCriterion].successBySeverity[severity] = (wcagStats[wcagCriterion].successBySeverity[severity] || 0) + 1;
        } else {
          wcagStats[wcagCriterion].failed++;
          wcagStats[wcagCriterion].failedBySeverity[severity] = (wcagStats[wcagCriterion].failedBySeverity[severity] || 0) + 1;
        }
        
        // T-006.3.2: Source attribution for fixes
        if (detail.axeCoreSource === true) {
          wcagStats[wcagCriterion].axeCoreFixCount++;
        } else if (detail.axeCoreSource === false) {
          wcagStats[wcagCriterion].llmFixCount++;
        }
        
        // T-006.3.1: Multi-criterion tracking
        if (Array.isArray(detail.wcagCriterion) && detail.wcagCriterion.length > 1) {
          wcagStats[wcagCriterion].multiCriterionFixCount++;
        }
        
        // T-006.3.3: Batch-fixing tracking
        if (detail.relatedIssues && detail.relatedIssues.length > 0) {
          wcagStats[wcagCriterion].batchFixCount++;
        }
      });
    });

    return wcagStats;
  }

  // Helper to render WCAG criteria breakdown for fixes
  function renderWcagFixBreakdown(wcagStats, allFixDetails) {
    const criteriaContainer = document.createElement('div');
    criteriaContainer.className = 'vae-wcag-fix-breakdown';

    const header = document.createElement('h5');
    header.textContent = 'Korrekturen nach WCAG-Kriterien:';
    header.style.marginBottom = '10px';
    criteriaContainer.appendChild(header);

    const statsGrid = document.createElement('div');
    statsGrid.className = 'vae-wcag-fix-stats-grid';
    statsGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 10px;
      margin-bottom: 15px;
    `;

    // Sort criteria by total count (descending)
    const sortedCriteria = Object.entries(wcagStats).sort((a, b) => b[1].total - a[1].total);

    sortedCriteria.forEach(([criterion, stats]) => {
      const statCard = document.createElement('div');
      statCard.className = 'vae-wcag-fix-stat-card vae-wcag-fix-expandable-card';
      statCard.style.cssText = `
        background: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 4px;
        padding: 12px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        cursor: pointer;
        transition: box-shadow 0.2s ease;
      `;

      const criterionName = criterion === 'Unknown' ? 'Unbekannt' : `WCAG ${criterion}`;
      const successRate = stats.total > 0 ? Math.round((stats.successful / stats.total) * 100) : 0;
      
      // Get fix details for this criterion
      const fixDetailsForCriterion = allFixDetails.filter(detail => {
        // T-006.3.1: Support both single criterion and multi-criterion fix results
        const criteria = Array.isArray(detail.wcagCriterion) ? detail.wcagCriterion : [detail.wcagCriterion || 'Unknown'];
        return criteria.includes(criterion);
      });

      const cardId = `wcag-fix-card-${criterion.replace('.', '-')}`;
      const detailsId = `wcag-fix-details-${criterion.replace('.', '-')}`;
      
      // T-006.3.2 & T-006.3.3: Source and batch processing info
      const fixSourceInfo = [];
      if (stats.axeCoreFixCount > 0) {
        fixSourceInfo.push(`🔍 Axe-Core: ${stats.axeCoreFixCount}`);
      }
      if (stats.llmFixCount > 0) {
        fixSourceInfo.push(`🤖 LLM: ${stats.llmFixCount}`);
      }
      if (stats.multiCriterionFixCount > 0) {
        fixSourceInfo.push(`🔗 Multi: ${stats.multiCriterionFixCount}`);
      }
      if (stats.batchFixCount > 0) {
        fixSourceInfo.push(`📎 Batch: ${stats.batchFixCount}`);
      }
      
      statCard.innerHTML = `
        <div id="${cardId}" style="position: relative;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div style="flex: 1;">
              <div style="font-weight: bold; margin-bottom: 6px; color: #495057;">${criterionName}</div>
              <div style="font-size: 1.1em; margin-bottom: 8px;">
                <span style="color: #007bff; font-weight: bold;">${stats.total} Korrekturen</span>
                <span style="color: #6c757d; font-size: 0.9em; margin-left: 8px;">(${successRate}% Erfolg)</span>
              </div>
              ${fixSourceInfo.length > 0 ? `<div style="font-size: 0.75em; color: #6c757d; margin-bottom: 8px;">${fixSourceInfo.join(' • ')}</div>` : ''}
            </div>
            <div style="margin-left: 8px; color: #6c757d; font-size: 0.8em;">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"></path>
              </svg>
            </div>
          </div>
          <div style="display: flex; gap: 8px; margin-bottom: 8px;">
            <span style="background: #28a745; color: white; padding: 4px 8px; border-radius: 3px; font-size: 0.9em;">
              ✓ ${stats.successful} Erfolgreich
            </span>
            <span style="background: #dc3545; color: white; padding: 4px 8px; border-radius: 3px; font-size: 0.9em;">
              ✗ ${stats.failed} Fehlgeschlagen
            </span>
          </div>
        </div>
        <div id="${detailsId}" style="display: none; margin-top: 12px; padding-top: 12px; border-top: 1px solid #dee2e6;">
          <div style="font-weight: bold; margin-bottom: 8px; color: #495057; font-size: 0.9em;">Einzelne Korrekturen:</div>
          <div style="max-height: 200px; overflow-y: auto;">
            ${fixDetailsForCriterion.map(detail => {
              const shortTarget = detail.target ? 
                (detail.target.length > 40 ? detail.target.substring(0, 40) + '...' : detail.target) : 
                'Kein Target';
              const statusIcon = detail.success ? '✓' : '✗';
              const statusColor = detail.success ? '#28a745' : '#dc3545';
              const extractedVaeId = extractVaeId(detail);
              
              // T-006.3.2 & T-006.3.3: Source and batch indicators for individual fixes
              const fixSourceIndicator = detail.axeCoreSource === true ? '🔍' : 
                                        detail.axeCoreSource === false ? '🤖' : '❓';
              const multiCriterionIndicator = Array.isArray(detail.wcagCriterion) && detail.wcagCriterion.length > 1 ? '🔗' : '';
              const batchIndicator = detail.relatedIssues && detail.relatedIssues.length > 0 ? '📎' : '';
              
              return `
                <div style="margin-bottom: 8px; padding: 8px; background: #ffffff; border: 1px solid #e9ecef; border-radius: 3px; font-size: 0.85em; cursor: pointer; transition: background-color 0.2s ease;"
                     data-vae-id="${extractedVaeId || ''}" 
                     data-target="${detail.target || ''}"
                     onmouseover="this.style.backgroundColor='#f0f8ff'"
                     onmouseout="this.style.backgroundColor='#ffffff'"
                     onclick="VAE.AnalysisDisplay.scrollToElement('${extractedVaeId || ''}', '${(detail.target || '').replace(/'/g, '\\\'')}')"
                >
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <span style="color: ${statusColor}; font-weight: bold; font-size: 1.1em;">${statusIcon}</span>
                      <strong style="color: #495057;">${displayVaeId(detail)}</strong>
                      <span style="font-size: 0.9em;">${fixSourceIndicator}${multiCriterionIndicator}${batchIndicator}</span>
                    </div>
                    <span style="background: ${getSeverityColor(detail.severity)}; color: ${getSeverityTextColor(detail.severity)}; padding: 2px 6px; border-radius: 2px; font-size: 0.8em;">
                      ${detail.severity || 'Unbekannt'}
                    </span>
                  </div>
                  <div style="margin-bottom: 4px;">
                    <span style="font-weight: bold; color: #495057; font-size: 0.9em;">Aktion:</span>
                    <span style="color: #007bff; margin-left: 4px;">${detail.action || 'Keine Aktion'}</span>
                  </div>
                  <div style="color: #6c757d; font-size: 0.8em; margin-bottom: 4px;" title="${detail.target || ''}">${shortTarget}</div>
                  ${detail.description ? `<div style="color: #495057; line-height: 1.3; margin-bottom: 4px;">${detail.description}</div>` : ''}
                  ${Array.isArray(detail.wcagCriterion) && detail.wcagCriterion.length > 1 ? 
                    `<div style="color: #6c757d; font-size: 0.75em; margin-top: 4px;">Betrifft auch: ${formatWcag(detail.wcagCriterion.filter(c => c !== criterion))}</div>` : 
                    ''}
                  ${!detail.success && detail.error ? `<div style="color: #dc3545; font-size: 0.8em; margin-top: 4px; padding: 4px; background: #f8d7da; border-radius: 2px;">Fehler: ${detail.error}</div>` : ''}
                  <div style="text-align: right; color: #6c757d; font-size: 0.75em; margin-top: 4px;">${detail.durationMs || 0} ms</div>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `;

      // Add hover effect
      statCard.addEventListener('mouseenter', () => {
        statCard.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
      });
      statCard.addEventListener('mouseleave', () => {
        statCard.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
      });

      // Add click handler for expansion
      statCard.addEventListener('click', (e) => {
        e.preventDefault();
        const detailsElement = document.getElementById(detailsId);
        const isExpanded = detailsElement.style.display === 'block';
        
        detailsElement.style.display = isExpanded ? 'none' : 'block';
        
        // Rotate the arrow
        const arrow = statCard.querySelector('svg');
        if (arrow) {
          arrow.style.transform = isExpanded ? 'rotate(0deg)' : 'rotate(180deg)';
          arrow.style.transition = 'transform 0.2s ease';
        }
      });

      statsGrid.appendChild(statCard);
    });

    criteriaContainer.appendChild(statsGrid);
    return criteriaContainer;
  }

  function showLoading(messageText = 'Analyse wird geladen...') {
    const targetElement = getAutoSectionElement();
    if (!targetElement) return;
    targetElement.innerHTML = `<div class="vae-loading-indicator" style="display: flex; align-items: center; justify-content: center; flex-direction: column; padding: 20px;">
                                 <div class="vae-loading-spinner"></div>
                                 <p style="margin-top: 10px;">${messageText}</p>
                               </div>`;
  }

  function showDisabledMessage(messageText = 'Automatische Übermittlung zur Analyse ist deaktiviert.') {
    const targetElement = getAutoSectionElement();
    if (!targetElement) return;
    targetElement.innerHTML = `<p class="vae-info-message" style="padding: 20px; text-align: center;">${messageText}</p>`;
  }

  function showProgress(progressPercentage, messageText = 'Analyse wird verarbeitet...') {
    const targetElement = getAutoSectionElement();
    if (!targetElement) return;
    targetElement.innerHTML = `<div class="vae-loading-indicator" style="display: flex; align-items: center; justify-content: center; flex-direction: column; padding: 20px;">
                                 <div class="vae-loading-spinner"></div>
                                 <div style="display: flex; align-items: center; margin-top: 10px; width: 100%; flex-direction: column;">
                                   <p id="vae-progress-text" style="margin: 0 0 5px 0;">${messageText} ${typeof progressPercentage === 'number' ? `(${progressPercentage}%)` : ''}</p>
                                   ${typeof progressPercentage === 'number' ? `<progress id="vae-progress-bar" max="100" value="${progressPercentage}" style="width: 80%; height: 10px;"></progress>` : ''}
                                 </div>
                               </div>`;
  }

  /**
   * NEW: Show detailed queue progress based on queue status polling data
   * Displays chunk progress, queue position, estimated wait time, and processing details
   */
    function showQueueProgress(queueStatus) {
    console.log('VAE.AnalysisDisplay: showQueueProgress called with:', queueStatus);
    const targetElement = getAutoSectionElement();
    if (!targetElement) {
      console.warn('VAE.AnalysisDisplay: Target element not found for showQueueProgress');
      return;
    }

    // Extract queue status data
    const {
      status = 'unknown',
      statusMessage: customStatusMessage = null,
      estimated_completion = null,
      chunk_progress = null,
      pollingMode = 'unknown'
    } = queueStatus || {};

    // NEW: Simple user-friendly display (Variante A)
    let statusMessage = 'Analyse läuft...';
    let timeInfo = '';
    
    // Use custom status message if provided
    if (customStatusMessage) {
      statusMessage = customStatusMessage;
    } else {
      // Handle different status states with user-friendly messages
      switch (status) {
        case 'initializing':
          statusMessage = 'Analyse wird gestartet...';
          break;
        case 'queued':
          statusMessage = 'Analyse läuft...';
          break;
        case 'processing':
        case 'analyzing':
          statusMessage = 'Analyse läuft...';
          break;
        case 'consolidating':
        case 'finalizing':
          statusMessage = 'Analyse wird abgeschlossen...';
          break;
        default:
          statusMessage = 'Analyse läuft...';
      }
    }

    // Calculate estimated completion time
    if (estimated_completion) {
      try {
        const completionTime = new Date(estimated_completion);
        const now = new Date();
        const diffMs = completionTime.getTime() - now.getTime();
        const diffMinutes = Math.ceil(diffMs / (1000 * 60));
        
        if (diffMinutes > 0) {
          if (diffMinutes === 1) {
            timeInfo = 'Fertig in ca. 1 Minute';
          } else if (diffMinutes < 60) {
            timeInfo = `Fertig in ca. ${diffMinutes} Minuten`;
          } else {
            // Format as time for longer durations
            const hours = completionTime.getHours().toString().padStart(2, '0');
            const minutes = completionTime.getMinutes().toString().padStart(2, '0');
            timeInfo = `Fertig um ca. ${hours}:${minutes} Uhr`;
          }
        } else {
          timeInfo = 'Wird gleich abgeschlossen...';
        }
      } catch (error) {
        console.warn('VAE.AnalysisDisplay: Error parsing estimated_completion:', error);
      }
    }

    console.log('VAE.AnalysisDisplay: Building simple UI with:', {
      statusMessage,
      timeInfo,
      estimated_completion
    });

    // Build the simple, user-friendly UI (Variante A)
    targetElement.innerHTML = `
      <div class="vae-loading-indicator" style="display: flex; align-items: center; justify-content: center; flex-direction: column; padding: 20px;">
        <div class="vae-loading-spinner"></div>
        <div style="display: flex; align-items: center; margin-top: 10px; width: 100%; flex-direction: column;">
          <p style="margin: 0 0 5px 0; font-weight: 500; font-size: 1.1em;">
            🔄 ${statusMessage}
          </p>
          ${timeInfo ? `
            <p style="margin: 5px 0; color: #28a745; font-weight: 500;">
              ⏰ ${timeInfo}
            </p>
          ` : ''}
          <div style="margin-top: 15px; font-size: 0.9em; color: #6c757d; text-align: center;">
            💡 Die Analyse läuft im Hintergrund
          </div>
        </div>
      </div>`;
  }

  function showErrorMessage(errorMessageText = 'Ein Fehler ist aufgetreten.') {
    const targetElement = getAutoSectionElement();
    if (!targetElement) return;
    targetElement.innerHTML = `<p class="vae-error-message" style="padding: 20px; text-align: center; color: red;">${errorMessageText}</p>`;
  }

  // Simplified version for now.
  // We can enhance this later with collapsible sections, severity, etc. from core.js.bak
  function renderInitialIssueSummary(analysisResults) {
    const targetElement = getAutoSectionElement();
    if (!targetElement) return;

    if (!analysisResults || !analysisResults.issues || !Array.isArray(analysisResults.issues) || analysisResults.issues.length === 0) {
      targetElement.innerHTML = '<p class="vae-no-issues-message" style="padding: 20px; text-align: center;">Keine Probleme gefunden oder Analyse noch nicht abgeschlossen.</p>';
      return;
    }

    targetElement.innerHTML = ''; // Clear previous content
    const summaryContainer = document.createElement('div');
    summaryContainer.className = 'vae-issue-summary-container'; // Consistent class

    const header = document.createElement('h4');
    header.textContent = `Gefundene Probleme (${analysisResults.issues.length}):`;
    summaryContainer.appendChild(header);

    // Add WCAG criteria breakdown
    const wcagStats = createWcagCriteriaStats(analysisResults.issues);
    const wcagBreakdown = renderWcagCriteriaBreakdown(wcagStats, analysisResults.issues);
    summaryContainer.appendChild(wcagBreakdown);

    // Group issues by severity for renderInitialIssueSummary as well
    const issuesBySeverity = {
      critical: [], high: [], medium: [], low: [], unknown: [], 'N/A': []
    };
    analysisResults.issues.forEach(issue => {
      const severity = (typeof issue.impact === 'string' ? issue.impact.toLowerCase() : 'unknown');
      (issuesBySeverity[severity] || issuesBySeverity.unknown).push(issue);
    });

    const severitiesToRender = ['critical', 'high', 'medium', 'low', 'unknown', 'N/A'];
    severitiesToRender.forEach(severityKey => {
        const issuesForSeverity = issuesBySeverity[severityKey];
        if (issuesForSeverity && issuesForSeverity.length > 0) {
            const sectionTitle = `${severityKey.charAt(0).toUpperCase() + severityKey.slice(1)} Probleme`;
            const section = createCollapsibleSection(
                sectionTitle,
                issuesForSeverity,
                `vae-severity-section vae-severity-${severityKey}`,
                false, // Default to collapsed
                (issue) => {
                    const item = document.createElement('li');
                    item.className = 'vae-issue-detail-item'; // Consistent class for styling
                    const selectorText = issue.selector ? issue.selector.substring(0, 50) + (issue.selector.length > 50 ? '...' : '') : 'Kein Selektor';
                    
                    // Check if issue is auto-fixable
                    const isAutoFixable = issue.isAutoFixable === true || 
                                         (issue.action && issue.action !== '' && issue.manual_fix_necessary !== true);
                    
                    item.innerHTML = `
                        <div class="vae-detail-header">
                            <span class="vae-detail-action">${formatActionForDisplay(issue)}</span>
                            <span class="vae-detail-severity vae-severity-${typeof issue.impact === 'string' ? issue.impact.toLowerCase() : 'unknown'}">${issue.impact || 'Unbekannt'}</span>
                            <span class="vae-detail-wcag">${formatWcag(issue.wcagCriterion)}</span>
                            ${isAutoFixable ? '<button class="vae-fix-button" title="Fix anwenden">🔧 Fix</button>' : '<span class="vae-manual-fix" title="Manuelle Korrektur erforderlich">⚠️ Manuell</span>'}
                        </div>
                        <div class="vae-detail-target" title="${issue.selector || ''}">
                            ${selectorText}${issue.vaeId ? ' (ID: ' + issue.vaeId + ')' : ''}
                        </div>
                        <div class="vae-issue-description">${issue.description || 'Kein Detail'}</div>
                    `;
                    
                    // Add click handler for issue highlighting
                    item.addEventListener('click', function (e) {
                        // Don't trigger if fix button was clicked
                        if (e.target.classList.contains('vae-fix-button')) return;
                        
                        let targetEl = null;
                        if (issue.vaeId) targetEl = document.getElementById(issue.vaeId);
                        if (!targetEl && issue.selector) {
                            try { targetEl = document.querySelector(issue.selector); } catch (e) { console.warn('Invalid selector for issue click:', issue.selector, e); }
                        }
                        if (targetEl) {
                            targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            const prevOutline = targetEl.style.outline;
                            targetEl.style.outline = '3px solid #ff0'; // Yellow highlight
                            setTimeout(() => { targetEl.style.outline = prevOutline; }, 2500);
                        }
                    });
                    
                    // Add click handler for fix button
                    if (isAutoFixable) {
                        const fixButton = item.querySelector('.vae-fix-button');
                        if (fixButton) {
                            fixButton.addEventListener('click', function (e) {
                                e.stopPropagation();
                                applyIndividualFix(issue, fixButton);
                            });
                        }
                    }
                    
                    return item;
                }
            );
            if (section) summaryContainer.appendChild(section);
        }
    });
    targetElement.appendChild(summaryContainer);
  }

  function renderFixSummary(successCount, failCount, resultsDetails) {
    const targetElement = getAutoSectionElement();
    if (!targetElement) return;

    targetElement.innerHTML = ''; // Clear previous content

    const summaryContainer = document.createElement('div');
    summaryContainer.className = 'vae-fix-summary-container';

    const totalIssues = successCount + failCount;
    const overviewHeader = document.createElement('div');
    overviewHeader.className = 'vae-fix-summary-header';
    overviewHeader.innerHTML = `
        <h4>Ergebnis der Korrekturen</h4>
        <div class="vae-fix-overview">
            <span class="vae-fix-count vae-fix-total-count">${totalIssues} Gesamt</span>
            <span class="vae-fix-count vae-fix-success-count">✓ ${successCount} Erfolgreich</span>
            <span class="vae-fix-count vae-fix-failure-count">✗ ${failCount} Fehlgeschlagen</span>
        </div>
    `;
    summaryContainer.appendChild(overviewHeader);

    // Add WCAG criteria breakdown for fixes
    if (resultsDetails && resultsDetails.length > 0) {
      const wcagStats = createWcagFixStats(resultsDetails);
      const wcagBreakdown = renderWcagFixBreakdown(wcagStats, resultsDetails);
      summaryContainer.appendChild(wcagBreakdown);
    }

    const detailsContainer = document.createElement('div');
    detailsContainer.className = 'vae-fix-details-container';

    const groups = {
      failed: { critical: [], high: [], medium: [], low: [], unknown: [], 'N/A': [] },
      success: { critical: [], high: [], medium: [], low: [], unknown: [], 'N/A': [] }
    };

    resultsDetails.forEach(detail => {
      const statusGroup = detail.success ? groups.success : groups.failed;
      const severityKey = (typeof detail.severity === 'string' ? detail.severity.toLowerCase() : 'unknown');
      (statusGroup[severityKey] || statusGroup.unknown).push(detail);
    });

    const renderDetailItem = (detail) => {
        const item = document.createElement('li');
        item.className = `vae-fix-detail-item ${detail.success ? 'vae-success' : 'vae-failure'}`;
        const targetText = detail.target ? detail.target.substring(0, 70) + (detail.target.length > 70 ? '...' : '') : 'N/A';
        item.innerHTML = `
            <div class="vae-detail-header">
                <span class="vae-detail-status">${detail.success ? '✓' : '✗'}</span>
                <strong class="vae-detail-action">${detail.action}</strong>
                <span class="vae-detail-severity vae-severity-${detail.severity.toLowerCase()}">${detail.severity}</span>
            </div>
            <div class="vae-detail-target" title="${detail.target || ''}">${targetText}${detail.vaeId ? ` (ID: ${detail.vaeId})` : ''}</div>
            <div class="vae-detail-description"><em>${detail.description || ''}</em></div>
            ${!detail.success && detail.error ? `<div class="vae-detail-error">Fehler: ${detail.error}</div>` : ''}
            <div class="vae-detail-meta">
                <span class="vae-detail-wcag">${formatWcag(detail.wcagCriterion)}</span>
                <span class="vae-detail-duration">(${detail.durationMs} ms)</span>
            </div>
        `;
        item.style.cursor = 'pointer';
        item.addEventListener('click', function () {
            let targetEl = null;
            if (detail.vaeId) targetEl = document.getElementById(detail.vaeId);
            if (!targetEl && detail.target && detail.target !== 'N/A') {
                try { targetEl = document.querySelector(detail.target); } catch (e) { console.warn('Invalid selector for fix detail click:', detail.target, e); }
            }
            if (targetEl) {
                targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const prevOutline = targetEl.style.outline;
                targetEl.style.outline = '3px solid #007bff'; // Blue highlight for fixes
                setTimeout(() => { targetEl.style.outline = prevOutline; }, 2500);
            }
        });
        return item;
    };

    if (failCount > 0) {
        const failedHeader = document.createElement('h5');
        failedHeader.textContent = 'Fehlgeschlagene Korrekturen:';
        detailsContainer.appendChild(failedHeader);
        const severities = ['critical', 'high', 'medium', 'low', 'unknown', 'N/A'];
        severities.forEach(severityKey => {
            const items = groups.failed[severityKey];
            if (items.length > 0) {
                const section = createCollapsibleSection(
                    `${severityKey.charAt(0).toUpperCase() + severityKey.slice(1)} (${items.length})`,
                    items,
                    `vae-severity-section vae-severity-${severityKey} vae-failed-items`,
                    severityKey === 'critical' || severityKey === 'high', // Open critical/high failed by default
                    renderDetailItem
                );
                if (section) detailsContainer.appendChild(section);
            }
        });
    }

    if (successCount > 0) {
        const successHeader = document.createElement('h5');
        successHeader.textContent = 'Erfolgreiche Korrekturen:';
        successHeader.style.marginTop = (failCount > 0) ? '20px' : '0';
        detailsContainer.appendChild(successHeader);
        const severities = ['critical', 'high', 'medium', 'low', 'unknown', 'N/A'];
        severities.forEach(severityKey => {
            const items = groups.success[severityKey];
            if (items.length > 0) {
                const section = createCollapsibleSection(
                    `${severityKey.charAt(0).toUpperCase() + severityKey.slice(1)} (${items.length})`,
                    items,
                    `vae-severity-section vae-severity-${severityKey} vae-success-items`,
                    false, // Success sections collapsed by default
                    renderDetailItem
                );
                if (section) detailsContainer.appendChild(section);
            }
        });
    }
    
    if (totalIssues === 0) {
        detailsContainer.innerHTML = '<p class="vae-info-message">Keine Korrekturen zum Anwenden vorhanden.</p>';
    }

    summaryContainer.appendChild(detailsContainer);
    targetElement.appendChild(summaryContainer);
  }

  // Helper function to scroll to an element and highlight it
  function scrollToElement(vaeId, selector) {
    let targetEl = null;
    
    // First try to find by VAE ID
    if (vaeId) {
      targetEl = document.getElementById(vaeId);
    }
    
    // If not found and we have a selector, try that
    if (!targetEl && selector && selector !== 'undefined' && selector !== '') {
      try {
        targetEl = document.querySelector(selector);
      } catch (e) {
        console.warn('Invalid selector for scrolling:', selector, e);
      }
    }
    
    if (targetEl) {
      // Scroll to element
      targetEl.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
      
      // Highlight the element
      const prevOutline = targetEl.style.outline;
      const prevTransition = targetEl.style.transition;
      
      targetEl.style.transition = 'outline 0.3s ease';
      targetEl.style.outline = '3px solid #007bff';
      
      // Remove highlight after 3 seconds
      setTimeout(() => {
        targetEl.style.outline = prevOutline;
        targetEl.style.transition = prevTransition;
      }, 3000);
    } else {
      console.warn('Element not found for scrolling. VAE ID:', vaeId, 'Selector:', selector);
    }
  }

  // Helper function to format action information for display
  function formatActionForDisplay(issue) {
    // If we have structured action info, format it nicely
    if (issue.action && typeof issue.action === 'string' && issue.actionParams) {
      return formatStructuredAction(issue.action, issue.actionParams);
    }
    
    // If we have a suggested action, use that
    if (issue.suggestedAction) {
      return `Vorschlag: ${issue.suggestedAction}`;
    }
    
    // If we have manual fix instructions, show abbreviated version
    if (issue.manualFixInstructions) {
      const shortInstruction = issue.manualFixInstructions.substring(0, 80);
      return `Manuell: ${shortInstruction}${issue.manualFixInstructions.length > 80 ? '...' : ''}`;
    }
    
    // Fall back to the basic action or default text
    return issue.action || 'Problembeschreibung';
  }

  // Helper function to format structured actions
  function formatStructuredAction(action, actionParams) {
    if (!actionParams) return action;
    
          // Handle different possible attribute name formats
      const getAttribute = () => {
        return actionParams.attribute || actionParams.attr || actionParams.name || actionParams.key;
      };
    
    const getValue = () => {
      return actionParams.value || actionParams.val || actionParams.content;
    };
    
    const getProperty = () => {
      return actionParams.property || actionParams.prop || actionParams.style;
    };
    
    switch (action) {
      case 'setAttribute':
        const attr = getAttribute();
        const val = getValue();
        return `Attribut setzen: ${attr || 'unbekannt'}="${val || ''}"`;
      case 'removeAttribute':
        const removeAttr = getAttribute();
        return `Attribut entfernen: ${removeAttr || 'unbekannt'}`;
      case 'setStyle':
        const prop = getProperty();
        const styleVal = getValue();
        return `CSS ändern: ${prop || 'unbekannt'}: ${styleVal || ''}`;
      case 'changeTag':
        return `Tag ändern zu: ${actionParams.newTag || actionParams.tag || 'unbekannt'}`;
      case 'setContent':
        return 'Inhalt ersetzen';
      case 'replaceElement':
        return 'Element ersetzen';
      case 'removeElement':
        return 'Element entfernen';
      case 'addElement':
        return `Element ${actionParams.position === 'after' ? 'nach' : actionParams.position === 'before' ? 'vor' : actionParams.position === 'append' ? 'in' : 'zu'} hinzufügen`;
      case 'wrap':
        return 'Element umschließen';
      case 'wrapInner':
        return 'Inhalt umschließen';
      default:
        return action;
    }
  }

  // Helper function to get detailed action information
  function getActionDetails(issue) {
    const details = [];
    
    // Add main action if available
    if (issue.action && issue.actionParams) {
      details.push(`<strong>Aktion:</strong> ${formatStructuredAction(issue.action, issue.actionParams)}`);
      
      // Add technical details for developers
      if (issue.actionParams.selector) {
        details.push(`<strong>Selektor:</strong> <code>${issue.actionParams.selector}</code>`);
      }
      if (issue.actionParams.attribute) {
        details.push(`<strong>Attribut:</strong> <code>${issue.actionParams.attribute}</code>`);
      }
      if (issue.actionParams.value) {
        details.push(`<strong>Wert:</strong> <code>${issue.actionParams.value}</code>`);
      }
    }
    
    // Add suggested action if different from main action
    if (issue.suggestedAction && issue.suggestedAction !== issue.action) {
      details.push(`<strong>Alternative:</strong> ${issue.suggestedAction}`);
    }
    
    // Add manual instructions if available
    if (issue.manualFixInstructions) {
      details.push(`<strong>Manuelle Anweisungen:</strong> ${issue.manualFixInstructions}`);
    }
    
    // Add fix confidence if available
    if (issue.fixConfidence !== null && issue.fixConfidence !== undefined) {
      const confidence = Math.round(issue.fixConfidence * 100);
      details.push(`<strong>Vertrauen:</strong> ${confidence}%`);
    }
    
    return details.length > 0 ? details.join('<br>') : null;
  }

  // Apply individual fix for a single issue
  function applyIndividualFix(issue, buttonElement) {
    if (!issue || !buttonElement) return;
    
    // Disable button and show loading state
    const originalText = buttonElement.textContent;
    buttonElement.disabled = true;
    buttonElement.textContent = '⏳ Anwenden...';
    buttonElement.classList.add('vae-fixing');
    
    // Try to apply the fix with fallback strategies
    applyFixWithFallback(issue, buttonElement, originalText);
  }
  
  // Apply fix with fallback strategies
  function applyFixWithFallback(issue, buttonElement, originalText) {
    // Strategy 1: Try VAE.Core.applyAccessibilityFixes
    if (typeof VAE !== 'undefined' && VAE.Core && typeof VAE.Core.applyAccessibilityFixes === 'function') {
      console.log('Using VAE.Core.applyAccessibilityFixes for fix application');
      VAE.Core.applyAccessibilityFixes([issue]).then((result) => {
        handleFixResult(result, buttonElement, issue);
      }).catch((error) => {
        console.error('VAE.Core.applyAccessibilityFixes failed, trying fallback:', error);
        applyManualFix(issue, buttonElement, originalText);
      });
      return;
    }
    
    // Strategy 2: Wait a bit and try again (Core might still be loading)
    console.log('VAE.Core not ready, waiting 1 second and retrying...');
    setTimeout(() => {
      if (typeof VAE !== 'undefined' && VAE.Core && typeof VAE.Core.applyAccessibilityFixes === 'function') {
        console.log('VAE.Core now available, retrying...');
        VAE.Core.applyAccessibilityFixes([issue]).then((result) => {
          handleFixResult(result, buttonElement, issue);
        }).catch((error) => {
          console.error('VAE.Core.applyAccessibilityFixes failed on retry, trying manual fix:', error);
          applyManualFix(issue, buttonElement, originalText);
        });
      } else {
        console.log('VAE.Core still not available, using manual fix');
        applyManualFix(issue, buttonElement, originalText);
      }
    }, 1000);
  }
  
  // Handle fix result
  function handleFixResult(result, buttonElement, issue) {
    if (result && result.successCount > 0) {
      buttonElement.textContent = '✅ Angewendet';
      buttonElement.classList.remove('vae-fixing');
      buttonElement.classList.add('vae-fixed');
      buttonElement.title = 'Fix erfolgreich angewendet';
      
      // Visually indicate the issue is fixed
      setTimeout(() => {
        const issueItem = buttonElement.closest('.vae-issue-detail-item');
        if (issueItem) {
          issueItem.style.opacity = '0.5';
          issueItem.style.textDecoration = 'line-through';
        }
      }, 1000);
    } else {
      buttonElement.textContent = '❌ Fehlgeschlagen';
      buttonElement.classList.remove('vae-fixing');
      buttonElement.classList.add('vae-fix-error');
      buttonElement.title = 'Fix konnte nicht angewendet werden';
      buttonElement.disabled = false;
    }
  }
  
  // Manual fix application as fallback
  function applyManualFix(issue, buttonElement, originalText) {
    console.log('Attempting manual fix application for issue:', issue);
    
    try {
      // Try to apply the fix manually based on action and actionParams
      if (issue.action && issue.actionParams) {
        const success = executeManualFix(issue.action, issue.actionParams, issue.selector || issue.vaeId);
        
        if (success) {
          buttonElement.textContent = '✅ Angewendet';
          buttonElement.classList.remove('vae-fixing');
          buttonElement.classList.add('vae-fixed');
          buttonElement.title = 'Fix manuell erfolgreich angewendet';
          
          // Visually indicate the issue is fixed
          setTimeout(() => {
            const issueItem = buttonElement.closest('.vae-issue-detail-item');
            if (issueItem) {
              issueItem.style.opacity = '0.5';
              issueItem.style.textDecoration = 'line-through';
            }
          }, 1000);
        } else {
          throw new Error('Manuelle Fix-Anwendung fehlgeschlagen');
        }
      } else {
        throw new Error('Keine ausführbare Action verfügbar');
      }
    } catch (error) {
      console.error('Manual fix application failed:', error);
      buttonElement.textContent = '❌ Nicht verfügbar';
      buttonElement.classList.remove('vae-fixing');
      buttonElement.classList.add('vae-fix-unavailable');
      buttonElement.title = `Fix nicht anwendbar: ${error.message}`;
      buttonElement.disabled = true;
    }
  }
  
  // Execute manual fix based on action type
  function executeManualFix(action, actionParams, elementIdentifier) {
    let elements = [];
    
    // Try to find the element(s)
    if (elementIdentifier) {
      if (elementIdentifier.startsWith('vae-')) {
        const element = document.getElementById(elementIdentifier);
        if (element) elements.push(element);
      } else {
        try {
          // Use querySelectorAll for selectors to handle multiple elements
          const nodeList = document.querySelectorAll(elementIdentifier);
          elements = Array.from(nodeList);
        } catch (e) {
          console.warn('Invalid selector:', elementIdentifier);
        }
      }
    }
    
    if (elements.length === 0) {
      console.error('No elements found for manual fix:', elementIdentifier);
      return false;
    }
    
    // Apply fix to all matching elements
    let successCount = 0;
    
    elements.forEach((element, index) => {
      try {
        let success = false;
        
        switch (action) {
          case 'setAttribute':
            let attr = null;
            let value = '';
            
            // Handle key/value structure (e.g., key="aria-label", value="Button text")
            if (actionParams.key && actionParams.value !== undefined) {
              attr = actionParams.key;
              value = actionParams.value;
            } else {
              // Handle direct attribute structure
              attr = actionParams.attribute || actionParams.attr || actionParams.name;
              value = actionParams.value || actionParams.val || actionParams.content || '';
            }
            
            if (attr) {
              element.setAttribute(attr, value);
              console.log(`Set attribute ${attr}="${value}" on element ${index + 1}/${elements.length}:`, element);
              success = true;
            } else {
              console.warn('setAttribute: Missing attribute name', { actionParams });
            }
            break;
           
          case 'removeAttribute':
            let removeAttr = null;
            
            // Handle key/value structure
            if (actionParams.key) {
              removeAttr = actionParams.key;
            } else {
              // Handle direct attribute structure
              removeAttr = actionParams.attribute || actionParams.attr || actionParams.name;
            }
            
            if (removeAttr && element.hasAttribute(removeAttr)) {
              element.removeAttribute(removeAttr);
              console.log(`Removed attribute ${removeAttr} from element ${index + 1}/${elements.length}:`, element);
              success = true;
            } else if (removeAttr) {
              console.log(`Attribute ${removeAttr} not found on element ${index + 1}/${elements.length}:`, element);
              success = true; // Consider it successful if attribute doesn't exist
            } else {
              console.warn('removeAttribute: Missing attribute name', { actionParams });
            }
            break;
           
          case 'setStyle':
            let property = null;
            let styleValue = null;
            
            // Handle key/value structure
            if (actionParams.key && actionParams.value !== undefined) {
              property = actionParams.key;
              styleValue = actionParams.value;
            } else {
              // Handle direct property structure
              property = actionParams.property || actionParams.prop || actionParams.style;
              styleValue = actionParams.value || actionParams.val || actionParams.content;
            }
            
            if (property && styleValue !== undefined) {
              element.style[property] = styleValue;
              console.log(`Set style ${property}: ${styleValue} on element ${index + 1}/${elements.length}:`, element);
              success = true;
            } else {
              console.warn('setStyle: Missing property or value', { actionParams });
            }
            break;
           
          case 'setContent':
            const content = actionParams.content || actionParams.value || actionParams.text;
            if (content !== undefined) {
              element.textContent = content;
              console.log(`Set content "${content}" on element ${index + 1}/${elements.length}:`, element);
              success = true;
            }
            break;

          case 'changeTag':
            // Handle different possible ways the new tag might be specified
            let newTag = null;
            if (actionParams.key === 'newTag' && actionParams.value) {
              newTag = actionParams.value;
            } else if (actionParams.newTag) {
              newTag = actionParams.newTag;
            } else if (actionParams.tag) {
              newTag = actionParams.tag;
            } else if (actionParams.value) {
              newTag = actionParams.value;
            }
            
            if (newTag && element.parentNode) {
              try {
                // Create new element with the desired tag
                const newElement = document.createElement(newTag);
                
                // Copy all attributes from old element to new element
                for (let i = 0; i < element.attributes.length; i++) {
                  const attr = element.attributes[i];
                  newElement.setAttribute(attr.name, attr.value);
                }
                
                // Copy all child nodes
                while (element.firstChild) {
                  newElement.appendChild(element.firstChild);
                }
                
                // Replace old element with new element
                element.parentNode.replaceChild(newElement, element);
                console.log(`Changed tag from ${element.tagName} to ${newTag} on element ${index + 1}/${elements.length}:`, newElement);
                success = true;
              } catch (error) {
                console.error('Error during tag change:', error);
              }
            } else {
              console.warn('changeTag: Missing newTag or element has no parent', { actionParams, element });
            }
            break;
           
          default:
            console.warn('Manual fix not implemented for action:', action);
        }
        
        if (success) successCount++;
        
      } catch (error) {
        console.error(`Error executing manual fix on element ${index + 1}/${elements.length}:`, error);
      }
    });
    
    console.log(`Applied fix to ${successCount}/${elements.length} elements`);
    return successCount > 0;
  }

  return {
    showLoading,
    showDisabledMessage,
    showProgress,
    showQueueProgress,
    showErrorMessage,
    renderInitialIssueSummary,
    renderFixSummary,
    scrollToElement
  };
})(); 